import { type MouseEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { SVGIcon } from '../icons/SVGIcon';

import styles from './Fab.module.scss';

export type FabProps = {
  /** Keep mounted while the assistant is open so the hide animation can play. */
  hidden?: boolean;
  /** Pill text revealed on hover. Defaults to the translated assistant name. */
  label?: string;
  onClick?: () => void;
};

function blurIfFocused(element: HTMLButtonElement | null) {
  if (element && element === document.activeElement) {
    element.blur();
  }
}

export function Fab({ hidden = false, label, onClick }: FabProps) {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const pillLabel = label ?? t('app.aiAssistant.assistantTitle');

  useEffect(() => {
    if (hidden) {
      blurIfFocused(buttonRef.current);
    }
  }, [hidden]);

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    event.currentTarget.blur();
    onClick?.();
  }

  return (
    <button
      aria-label={t('app.aiAssistant.openAssistant')}
      className={clsx(styles['root'], hidden && styles['root--hidden'])}
      inert={hidden}
      onClick={handleClick}
      ref={buttonRef}
      tabIndex={hidden ? -1 : undefined}
      type="button"
      data-testid="ai-assistant-fab"
    >
      <span className={styles['label']}>{pillLabel}</span>
      <SVGIcon className={styles['icon']} name="aiIcon" />
    </button>
  );
}
